/**
 * The three destinations whose screens are sections of one page: KYC
 * configuration, billing and system. The section bar under the app bar picks
 * the section (menuStructure); each page renders the one the URL names.
 */

import React from 'react';
import { usePermissions, ResourceContextProvider } from 'react-admin';
import { Navigate } from 'react-router-dom';
import { useActiveSection } from '@/layouts/useSectionTabs';
import { userLevel } from '@/layouts/menuStructure';

import { ServerList } from '@/features/servers/ServerList';
import { LicenseList } from '@/features/licenses/LicenseList';
import { DocumentTypeList } from '@/features/document-types/DocumentTypeList';
import { NationalityList } from '@/features/nationalities/NationalityList';
import { KycFieldList } from '@/features/kyc-fields/KycFieldList';
import { FieldMappingList } from '@/features/fields-mapping/FieldMappingList';
import { DefaultMappingList } from '@/features/default-mapping/DefaultMappingList';
import { UserList } from '@/features/users/UserList';
import { CostCenterList } from '@/features/cost-centers/CostCenterList';
import { CreditsPage } from '@/features/credits/CreditsPage';
import { PricingPage } from '@/features/pricing/PricingPage';
import { BackupsPage } from '@/features/backups';

const isAdmin = (role?: string) => role === 'Administrator';

/* ── KYC Config: document types, nationalities, fields, mappings (admin only) ── */
export const KycConfigPage = () => {
  const { permissions: role, isLoading } = usePermissions<string>();
  const active = useActiveSection();

  // The rail entry is gated; this blocks a typed URL too.
  if (isLoading) return null;
  if (!isAdmin(role)) return <Navigate to="/" replace />;

  switch (active) {
    case 'nationalities':
      return <ResourceContextProvider value="nationalities"><NationalityList /></ResourceContextProvider>;
    case 'kyc-fields':
      return <ResourceContextProvider value="kyc-fields"><KycFieldList /></ResourceContextProvider>;
    case 'fields-mapping':
      return <ResourceContextProvider value="fields-mapping"><FieldMappingList /></ResourceContextProvider>;
    case 'default-mapping':
      return <ResourceContextProvider value="default-mapping"><DefaultMappingList /></ResourceContextProvider>;
    default:
      return <ResourceContextProvider value="document-types"><DocumentTypeList /></ResourceContextProvider>;
  }
};

/* ── Billing: cost centers, credits, pricing ── */
export const BillingPage = () => {
  const { permissions: role, isLoading } = usePermissions<string>();
  const active = useActiveSection();

  if (isLoading) return null;
  if (userLevel(role) < userLevel('AdvancedUser')) return <Navigate to="/" replace />;

  switch (active) {
    case 'credits':
      return <CreditsPage />;
    case 'pricing':
      // Administrators only; the section bar already hides it from anyone else.
      return isAdmin(role) ? <PricingPage /> : <Navigate to="/billing" replace />;
    default:
      return <ResourceContextProvider value="cost-centers"><CostCenterList /></ResourceContextProvider>;
  }
};

/* ── System: servers, licenses, users, backups (admin only) ── */
export const SystemPage = () => {
  const { permissions: role, isLoading } = usePermissions<string>();
  const active = useActiveSection();

  if (isLoading) return null;
  if (!isAdmin(role)) return <Navigate to="/" replace />;

  switch (active) {
    case 'licenses':
      return <ResourceContextProvider value="licenses"><LicenseList /></ResourceContextProvider>;
    case 'users':
      return <ResourceContextProvider value="users"><UserList /></ResourceContextProvider>;
    case 'backups':
      return <BackupsPage />;
    default:
      return <ResourceContextProvider value="servers"><ServerList /></ResourceContextProvider>;
  }
};
